/**
 * Transaction Utilities for Firestore Operations
 * 
 * Provides reusable transaction wrapper utilities with built-in error handling,
 * configurable retry logic, and exponential backoff for frontend (AngularFire) operations.
 * 
 * These utilities ensure atomic read-modify-write operations to prevent race conditions
 * and data corruption during concurrent Firestore operations.
 * 
 * @module transaction-utils
 */

import { Firestore, Transaction, runTransaction, doc } from 'firebase/firestore';

/**
 * Configuration options for transaction execution
 */
export interface TransactionOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds for exponential backoff (default: 100) */
  baseDelayMs?: number;
  /** Maximum delay in milliseconds for exponential backoff (default: 5000) */
  maxDelayMs?: number;
  /** Prefix for logging context (e.g., "BoardUpdate") */
  logPrefix?: string;
}

/**
 * Result of a transaction execution with metadata
 */
export interface TransactionResult<T> {
  /** Whether the transaction succeeded */
  success: boolean;
  /** The return value from the transaction function (if successful) */
  data?: T;
  /** Error details (if failed) */
  error?: TransactionError;
  /** Number of attempts made */
  attempts: number;
  /** Total duration in milliseconds */
  durationMs: number;
}

/**
 * Enhanced error type for transaction failures
 */
export interface TransactionError extends Error {
  /** Firestore error code (e.g., 'ABORTED', 'PERMISSION_DENIED') */
  code: string;
  /** Whether this error should be retried */
  isRetryable: boolean;
  /** Which attempt number this error occurred on */
  attemptNumber: number;
  /** Original error from Firestore */
  originalError?: Error;
}

/**
 * Options for read-modify-write pattern
 */
export interface ReadModifyWriteOptions<T> extends TransactionOptions {
  /** Firestore document path */
  docPath: string;
  /** Function to transform the current data */
  modifier: (currentData: T) => T;
}

/**
 * Item specification for batch transaction updates
 */
export interface BatchUpdateItem<T = any> {
  /** Firestore document path */
  docPath: string;
  /** Function to generate update object from current data */
  updateFn: (currentData: T) => Partial<T>;
}

/**
 * Default configuration for transaction operations
 */
const DEFAULT_OPTIONS: Required<TransactionOptions> = {
  maxRetries: 3,
  baseDelayMs: 100,
  maxDelayMs: 5000,
  logPrefix: 'Transaction'
};

/**
 * Sleep helper function for delays
 * @param ms Milliseconds to sleep
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Classify if an error is retryable based on Firestore error codes
 * 
 * Retryable errors:
 * - ABORTED: Transaction conflict
 * - FAILED_PRECONDITION: Transaction precondition failed
 * - UNAVAILABLE: Service temporarily unavailable
 * - DEADLINE_EXCEEDED: Request timeout
 * 
 * Non-retryable errors:
 * - NOT_FOUND: Document doesn't exist
 * - PERMISSION_DENIED: User lacks permission
 * - INVALID_ARGUMENT: Invalid request
 */
function isRetryableError(error: any): boolean {
  const code = error?.code || error?.message || '';
  const retryableCodes = ['ABORTED', 'FAILED_PRECONDITION', 'UNAVAILABLE', 'DEADLINE_EXCEEDED'];
  return retryableCodes.some(c => code.includes(c));
}

/**
 * Calculate exponential backoff delay with jitter
 * 
 * Formula: delay = min(baseDelay * 2^attempt, maxDelay)
 * Jitter: add 0-10% random variation to prevent thundering herd
 * 
 * @param attempt Current attempt number (0-indexed)
 * @param baseDelayMs Base delay in milliseconds
 * @param maxDelayMs Maximum delay cap in milliseconds
 * @returns Delay in milliseconds
 */
function calculateBackoffDelay(attempt: number, baseDelayMs: number, maxDelayMs: number): number {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);
  const jitter = Math.random() * cappedDelay * 0.1;
  return cappedDelay + jitter;
}

/**
 * Create a formatted transaction error
 */
function createTransactionError(
  originalError: any,
  attemptNumber: number,
  isRetryable: boolean
): TransactionError {
  const error: TransactionError = new Error(
    `Transaction failed: ${originalError?.message || 'Unknown error'}`
  ) as TransactionError;
  error.code = originalError?.code || 'UNKNOWN';
  error.isRetryable = isRetryable;
  error.attemptNumber = attemptNumber;
  error.originalError = originalError;
  return error;
}

/**
 * Core transaction wrapper with retry logic
 * 
 * Executes a Firestore transaction with automatic retry on contention errors,
 * exponential backoff, and comprehensive logging.
 * 
 * @template T Return type of the transaction function
 * @param db Firestore instance
 * @param transactionFn Function to execute within transaction context
 * @param options Configuration options (uses defaults if not provided)
 * @returns Transaction result with metadata
 * 
 * @example
 * ```typescript
 * const result = await executeTransaction(
 *   db,
 *   async (transaction) => {
 *     const docRef = doc(db, 'boards', boardId);
 *     const docSnap = await transaction.get(docRef);
 *     const newData = { state: 'updated', timestamp: Date.now() };
 *     transaction.update(docRef, newData);
 *     return newData;
 *   },
 *   { logPrefix: 'BoardUpdate', maxRetries: 5 }
 * );
 * 
 * if (result.success) {
 *   console.log('Updated data:', result.data);
 * } else {
 *   console.error('Transaction failed:', result.error);
 * }
 * ```
 */
export async function executeTransaction<T>(
  db: Firestore,
  transactionFn: (transaction: Transaction) => Promise<T>,
  options?: TransactionOptions
): Promise<TransactionResult<T>> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const startTime = Date.now();
  let lastError: Error | null = null;

  console.log(`[Transaction:${config.logPrefix}] Starting transaction...`);

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      const data = await runTransaction(db, transactionFn);
      const durationMs = Date.now() - startTime;

      console.log(
        `[Transaction:${config.logPrefix}] Completed in ${durationMs}ms after ${attempt + 1} attempt(s)`
      );

      return {
        success: true,
        data,
        attempts: attempt + 1,
        durationMs
      };
    } catch (error) {
      const err = error as any;
      lastError = err;
      const isRetryable = isRetryableError(err);
      const attemptNumber = attempt + 1;

      if (!isRetryable || attempt === config.maxRetries) {
        // Non-retryable error or final attempt
        const durationMs = Date.now() - startTime;
        console.error(
          `[Transaction:${config.logPrefix}] Failed after ${attemptNumber} attempt(s): ${err?.code || 'UNKNOWN'}`
        );

        return {
          success: false,
          error: createTransactionError(err, attemptNumber, false),
          attempts: attemptNumber,
          durationMs
        };
      }

      // Retryable error - wait and retry
      const delayMs = calculateBackoffDelay(attempt, config.baseDelayMs, config.maxDelayMs);
      console.warn(
        `[Transaction:${config.logPrefix}] Retry attempt ${attemptNumber + 1}/${config.maxRetries + 1}, delay: ${Math.round(delayMs)}ms`
      );

      await sleep(delayMs);
    }
  }

  // Should not reach here, but handle gracefully
  const durationMs = Date.now() - startTime;
  return {
    success: false,
    error: createTransactionError(lastError || new Error('Unknown error'), config.maxRetries + 1, false),
    attempts: config.maxRetries + 1,
    durationMs
  };
}

/**
 * Read-modify-write pattern wrapper
 * 
 * Convenience function for the common pattern of reading a document,
 * applying a transformation, and writing it back atomically.
 * 
 * @template T Type of the document data
 * @param db Firestore instance
 * @param options Configuration including doc path and modifier function
 * @returns The modified document data
 * 
 * @throws {TransactionError} If document not found or transaction fails
 * 
 * @example
 * ```typescript
 * interface BoardState {
 *   queue: string[];
 *   up: string | null;
 * }
 * 
 * const updatedState = await readModifyWrite<BoardState>(db, {
 *   docPath: 'boards/board123/state/current',
 *   modifier: (current) => ({
 *     ...current,
 *     up: current.queue[0] || null
 *   }),
 *   logPrefix: 'QueueRotation'
 * });
 * ```
 */
export async function readModifyWrite<T>(
  db: Firestore,
  options: ReadModifyWriteOptions<T>
): Promise<T> {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const result = await executeTransaction<T>(
    db,
    async (transaction) => {
      const docRef = doc(db, options.docPath);
      const docSnap = await transaction.get(docRef);

      if (!docSnap.exists()) {
        throw new Error(`Document not found: ${options.docPath}`);
      }

      const currentData = docSnap.data() as T;
      const modifiedData = options.modifier(currentData);

      transaction.update(docRef, modifiedData as any);

      return modifiedData;
    },
    config
  );

  if (!result.success) {
    throw result.error;
  }

  return result.data as T;
}

/**
 * Batch transaction update for multiple documents
 * 
 * Atomically updates multiple documents with read-before-write semantics.
 * All reads happen first, then all writes are applied atomically.
 * 
 * @param db Firestore instance
 * @param updates Array of document updates with path and update function
 * @param options Transaction configuration options
 * 
 * @throws {TransactionError} If any document is not found or transaction fails
 * 
 * @example
 * ```typescript
 * await batchTransactionUpdate(db, [
 *   {
 *     docPath: 'boards/board1/users/user1',
 *     updateFn: (current) => ({ state: 'up' })
 *   },
 *   {
 *     docPath: 'boards/board1/users/user2',
 *     updateFn: (current) => ({ state: 'on_deck' })
 *   }
 * ], { logPrefix: 'QueueRotation' });
 * ```
 */
export async function batchTransactionUpdate(
  db: Firestore,
  updates: BatchUpdateItem[],
  options?: TransactionOptions
): Promise<void> {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const result = await executeTransaction<void>(
    db,
    async (transaction) => {
      // Read all documents first
      const docSnapshots = await Promise.all(
        updates.map(update => transaction.get(doc(db, update.docPath)))
      );

      // Verify all documents exist
      docSnapshots.forEach((snap, index) => {
        if (!snap.exists()) {
          throw new Error(`Document not found: ${updates[index].docPath}`);
        }
      });

      // Apply updates atomically
      docSnapshots.forEach((snap, index) => {
        const currentData = snap.data();
        const updateData = updates[index].updateFn(currentData);
        transaction.update(snap.ref, updateData);
      });
    },
    config
  );

  if (!result.success) {
    throw result.error;
  }
}
